/**
 * This file handles the keycloak login state and exports an interface to interact with it.
 */
import EventEmitter from "events";
import type { LOGIN_PAYLOAD } from "@sinch/bus";
import type { KeycloakConfig, KeycloakInitOptions } from "keycloak-js";
import Keycloak from "keycloak-js";
import { matchPath } from "../components/Router/utils";
import { API_BASE_URL, AUTH_CONFIG } from "../config/legacy-frozen-config";
import { nonLoggedInRoutesConfig } from "../navigationConfig";
import type { ExchangedToken } from "../types/keycloakTypes";
import type { AuthStrategy, Token } from "./types";

// Every `refreshIntervalS` we check that we will have at
// least `tokenMinimumValidityGracePeriodS` time left for the next check.
// Always updating the token with at least `tokenMinimumValidityGracePeriodS`
// time minimum left before expiry.
const refreshIntervalS = 10;
const tokenMinimumValidityGracePeriodS = 30;

const kcInitOptions: KeycloakInitOptions = {
  onLoad: "check-sso",
  pkceMethod: "S256",
  silentCheckSsoRedirectUri: location.origin + "/silent-check-sso.html",
  checkLoginIframe: false,
  redirectUri: location.href,
};

// This should be a singleton as far as I can tell. Initializing outside of the hook.
let keycloak: Keycloak | undefined;

let initP: Promise<boolean> | undefined;

// Need to have some sort of event dispatching since the keycloak sdk only
// allows you to set one function for each event, but you might want to use the hook in multiple places.
let eventSource: EventEmitter | undefined;

// Not happy wih this super imperative code.. any ideas? Are we allowed to use some FRP lib? :)
let interval: number | null = null;

const getKeycloakInstance = () => {
  if (!keycloak) {
    if (!AUTH_CONFIG.url) {
      throw new Error("Cannot retrieve Keycloak instance: Auth URL not set.");
    }
    const kcConfig: KeycloakConfig = {
      url: AUTH_CONFIG.url,
      realm: "Chatlayer",
      clientId: "sinch-engage",
    };
    try {
      keycloak = new Keycloak(kcConfig);
    } catch (error) {
      console.error("getKeycloakInstance_failed: ", error);
      throw Error(`getKeycloakInstance_failed: ${JSON.stringify(error)}`);
    }
  }
  return keycloak;
};

const getEventEmitterInstance = () => {
  if (!eventSource) {
    eventSource = new EventEmitter();
    eventSource.setMaxListeners(20);
  }
  return eventSource;
};

const setupKeycloakEvents = () => {
  // Setup the emitter, from now on, use the emitter to access these events.
  getEventEmitterInstance().on("onAuthSuccess", () => {
    if (interval === null) {
      interval = window.setInterval(() => {
        getKeycloakInstance()
          .updateToken(refreshIntervalS + tokenMinimumValidityGracePeriodS)
          .catch(() => {
            console.error(
              `Could not refresh the token. Trying again in ${refreshIntervalS}s.`
            );
          });
      }, refreshIntervalS * 1000);
    }
  });

  getEventEmitterInstance().on("onAuthLogout", () => {
    if (interval !== null) {
      window.clearInterval(interval);
      interval = null;
      getKeycloakInstance().logout();
    }
  });

  getEventEmitterInstance().on("onTokenExpired", () => {
    getKeycloakInstance()
      .updateToken(0)
      .catch(() => {
        console.error("Could not refresh the token, logging out.");
        getKeycloakInstance().logout();
      });
  });
  getKeycloakInstance().onActionUpdate = handleTokenEvent("onActionUpdate");
  getKeycloakInstance().onAuthSuccess = handleTokenEvent("onAuthSuccess");
  getKeycloakInstance().onAuthError = handleTokenEvent("onAuthError");
  getKeycloakInstance().onAuthRefreshSuccess = handleTokenEvent(
    "onAuthRefreshSuccess"
  );
  getKeycloakInstance().onAuthRefreshError =
    handleTokenEvent("onAuthRefreshError");
  getKeycloakInstance().onAuthLogout = handleTokenEvent("onAuthLogout");
  getKeycloakInstance().onTokenExpired = handleTokenEvent("onTokenExpired");
};

export const initKeycloak: AuthStrategy["init"] = () => {
  setupKeycloakEvents();
  const isAuthScreen = () =>
    nonLoggedInRoutesConfig
      .map((route) => route.path)
      .some((authPath) => matchPath(authPath, location.pathname));

  initP = getKeycloakInstance().init(kcInitOptions);
  initP
    .then((isAuthenticated) => {
      getKeycloakInstance().redirectUri = location.origin;
      if (!isAuthenticated && !isAuthScreen())
        getKeycloakInstance().login({ redirectUri: location.href });
    })
    .catch((error) => console.error("Keycloak init error:", error));
};

const handleTokenEvent = (type: string) => () => {
  getEventEmitterInstance().emit(type, getToken());
};

export const onTokenUpdate: AuthStrategy["onTokenUpdate"] = (
  callback: (token: Token) => void
) => {
  getEventEmitterInstance().on("onAuthSuccess", callback);
  getEventEmitterInstance().on("onAuthRefreshSuccess", callback);
  getEventEmitterInstance().on("onAuthError", callback);
  getEventEmitterInstance().on("onAuthRefreshError", callback);
  getEventEmitterInstance().on("onAuthLogout", callback);
  getEventEmitterInstance().on("onTokenExpired", callback);

  return () => {
    getEventEmitterInstance().off("onAuthSuccess", callback);
    getEventEmitterInstance().off("onAuthRefreshSuccess", callback);
    getEventEmitterInstance().off("onAuthError", callback);
    getEventEmitterInstance().off("onAuthRefreshError", callback);
    getEventEmitterInstance().off("onAuthLogout", callback);
    getEventEmitterInstance().off("onTokenExpired", callback);
  };
};

// Token received from the backend are generated using a different client-id
// so we need to perform an exchange. Read More:
// https://www.keycloak.org/docs/latest/securing_apps/index.html#how-token-exchange-works
export const exchangeToken: AuthStrategy["exchangeToken"] = (
  tokenPayload: LOGIN_PAYLOAD
) =>
  window.fetch(
    `${AUTH_CONFIG.url}/realms/Chatlayer/protocol/openid-connect/token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body: new URLSearchParams({
        client_id: "sinch-engage",
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
        subject_token: tokenPayload.token,
      }),
    }
  );

export const forceInitWithExchangedToken: AuthStrategy["forceInitWithExchangedToken"] =
  async (token: ExchangedToken) => {
    initP = getKeycloakInstance().init({
      token: token.access_token,
      refreshToken: token.refresh_token,
      checkLoginIframe: false,
    });

    return initP;
  };

// Placeholder which we will replace in all calls to fetch and XMLHttpRequest.
// It must be url-safe and also quite unique. So, quoting RFC3986
// (https://www.ietf.org/rfc/rfc3986.txt)
//
// 2.3.  Unreserved Characters
//
//   Characters that are allowed in a URI but do not have a reserved
//   purpose are called unreserved.  These include uppercase and lowercase
//   letters, decimal digits, hyphen, period, underscore, and tilde.
//
//      unreserved  = ALPHA / DIGIT / "-" / "." / "_" / "~"
//
// Leaves us with the characters we can use safely.
//
// We also exclude "." to be regex safe.
//
const symbolicToken = "___---TOKEN-PLACEHOLDER---___";

export const login: AuthStrategy["login"] = (
  ...param: Parameters<Keycloak["login"]>
) => getKeycloakInstance().login(...param);
export const logout: AuthStrategy["logout"] = (
  ...param: Parameters<Keycloak["logout"]>
) => getKeycloakInstance().logout(...param);

export const getToken: AuthStrategy["getToken"] = () => {
  const currentKc = getKeycloakInstance();
  return currentKc.token && currentKc.tokenParsed
    ? {
        token: symbolicToken,
        parsedToken: currentKc.tokenParsed,
      }
    : null;
};

export const getTokenP: AuthStrategy["getTokenP"] = (): Promise<
  NonNullable<ReturnType<typeof getToken>>
> =>
  (
    initP ??
    Promise.reject(new Error("You need to call initKeycloak before getTokenP"))
  ).then((isAuthenticated) => {
    if (!isAuthenticated) {
      throw new Error("Not authenticated");
    }
    const token = getToken();
    if (!token) {
      throw new Error("Couldn't retrieve token");
    }
    return token;
  });

// Monkey patch code where the token is used to replace fake token with real.
// These are the things patched:
//
// * fetch
// * XMLHttpRequest
// * window.open
// * WebSocket
//
export const patchRequests: AuthStrategy["patchRequests"] = () => {
  const apiHostname = new URL(API_BASE_URL).hostname;

  const shouldReplaceToken = (url: Request | URL | string) => {
    try {
      const hostname = (
        url instanceof URL
          ? url
          : new URL(url instanceof Request ? url.url : url)
      ).hostname;

      return (
        hostname.endsWith(apiHostname) ||
        hostname.endsWith(".sinch.com") ||
        hostname.endsWith(".messengerpeople.com") ||
        hostname.endsWith(".chatlayer.ai") ||
        hostname.endsWith(".wbcsrv.com") ||
        hostname == "localhost"
      );
    } catch (error) {
      console.error("shouldReplaceToken_failed: ", error);
      return false;
    }
  };

  const placeholderRegEx = new RegExp(symbolicToken, "g");
  const replaceToken = (value: string) =>
    value?.replace?.(placeholderRegEx, getKeycloakInstance().token ?? "N/A") ??
    value;

  //////////////////////////////////////////////////////////////////////////////
  // Patch fetch
  const { fetch: originalFetch } = window;
  window.fetch = (...args) => {
    const patchedRequest = new Request(...args);
    const replace = shouldReplaceToken(patchedRequest.url);

    patchedRequest.headers.forEach((value, name) => {
      patchedRequest.headers.set(name, replace ? replaceToken(value) : value);
    });

    return originalFetch(patchedRequest);
  };

  //////////////////////////////////////////////////////////////////////////////
  // Patch XMLHttpRequest
  const { setRequestHeader } = XMLHttpRequest.prototype;
  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    // TODO: We should only replace the token if the request is to our own APIs.
    // Perhaps do it the same way as we do for the x-project-id header.
    // eslint-disable-next-line no-constant-condition
    if (true) {
      return setRequestHeader.call(this, name, replaceToken(value));
    }
    return setRequestHeader.call(this, name, value);
  };
  //////////////////////////////////////////////////////////////////////////////
  // Patch window.open
  // Really shouldn't send tokens in URL. Code doing this should be refactored
  // at some point...
  const windowOpen = window.open;
  window.open = (url, ...rest) =>
    windowOpen(
      url && shouldReplaceToken(url)
        ? replaceToken(new URL(url, location.href).href)
        : url,
      ...rest
    );

  //////////////////////////////////////////////////////////////////////////////
  // Patch WebSocket
  const { send } = WebSocket.prototype;
  WebSocket.prototype.send = function (data) {
    return send.call(this, typeof data == "string" ? replaceToken(data) : data);
  };

  // reset to original
  return () => {
    window.open = windowOpen;
    window.fetch = originalFetch;
    XMLHttpRequest.prototype.setRequestHeader = setRequestHeader;
    WebSocket.prototype.send = send;
  };
};
